import type { Dispatch, SetStateAction } from "react";
import type { AppStateValue } from "../../../../AppState";
import type { EditTicketAttachmentsProps } from "./EditableTicketAttachment";

type DeleteFileApi = {
  deleteFile: (id: number) => Promise<{ status?: number } | undefined>;
};

export type DeleteFileAttemptResult = {
  success: boolean;
  fileName: string;
  fileId: number;
  error?: unknown;
};

export async function deleteTicketFilesViaApi(
  api: DeleteFileApi,
  files: EditTicketAttachmentsProps[]
): Promise<{ successful: DeleteFileAttemptResult[]; failed: DeleteFileAttemptResult[] }> {
  const deletePromises = files.map(async (file: EditTicketAttachmentsProps) => {
    try {
      const deleteResponse = await api.deleteFile(file.Id);
      if (deleteResponse?.status === 204) {
        return { success: true, fileName: file.FileName, fileId: file.Id };
      }
      console.warn(`Failed to delete file: ${file.FileName}`, deleteResponse);
      return { success: false, fileName: file.FileName, fileId: file.Id, error: deleteResponse };
    } catch (error) {
      console.error(`Error deleting file ${file.FileName}:`, error);
      return { success: false, fileName: file.FileName, fileId: file.Id, error };
    }
  });

  const deleteResults = await Promise.all(deletePromises);
  const successful = deleteResults.filter((r) => r.success);
  const failed = deleteResults.filter((r) => !r.success);

  if (failed.length > 0) {
    console.warn("Some files failed to delete:", failed.map((f) => f.fileName));
  }

  return { successful, failed };
}

export function removeDeletedFileIdsFromUploadingState(
  setAppState: Dispatch<SetStateAction<AppStateValue>>,
  deletedFileIds: Set<number>
) {
  setAppState((state) => ({
    ...state,
    uploadingState: {
      ...state.uploadingState,
      uploadedFiles: state.uploadingState.uploadedFiles.filter((file) => !deletedFileIds.has(file.Id)),
    },
  }));
}
